import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { Profile } from '@app/models/account/profile';
import { DivisionPolicy } from '@app/models/ebo/division/policy/division-policy';
import { UserExperience } from '@app/models/ebo/shared/user-experience';
import { TenantFeatures } from '@app/models/ebo/tenant/tenant-features';
import { ServiceLocator } from '@summit/core';
import { SummitMenuItem, SummitUserExperienceService, ViewChoices } from '@summit/models';
import { LayoutService } from 'libs/summit/ui/src/lib/services/layout.service';

import { NavMenuItem } from '@app/config/nav-menu-item';
import { AuthenticationService } from './authentication/authentication.service';
import { CrudService } from './crud/crud.service';

const debug = false;
interface NavigatorApp {
  getInstalledRelatedApps(): Promise<unknown[]>;
}
@Injectable({ providedIn: 'root' })
export class UserExperienceService extends SummitUserExperienceService<UserExperience> {
  private crud?: CrudService;

  constructor(
    private authService: AuthenticationService,
    //private preloadService: OnDemandPreloadService,
    private layoutService: LayoutService
  ) {
    super();
    let loadExperience = JSON.parse(localStorage.getItem('currentUserExperience') ?? 'null');
    if (loadExperience) {
      //console.log('Loading Ux');
      loadExperience = new UserExperience(loadExperience);
    } else {
      //console.log('No User Experience Found - creating blank');
      loadExperience = new UserExperience();
    }
    if (debug) console.log('[UX] Initial User Experience:', loadExperience);
    this.currentUserExperienceSubject = new BehaviorSubject<UserExperience>(loadExperience);
    this.currentUserExperience = this.currentUserExperienceSubject.asObservable();
    if (loadExperience.isLoggedIn()) {
      this.layoutService.showSidebar();
    }
    //.pipe(
    //.tap((ux) => console.log('[UX] User Experience Changed:', ux)))
    //Activate Preload strategy for this user
    // this.currentUserExperience.subscribe((newUx) => {
    //   // No Unsubscribe
    //   this.preLoadBasedOnUx(newUx);
    // });
    // //breakpointObserver.observe
    // //Since this triggers too early for the route.
    // setTimeout(() => {
    //   this.preLoadBasedOnUx();
    // }, 0);
    //console.log(this.online$, window)

    //console.log(this.online$)
    //         .activeProfile$
    authService.activeProfile$.pipe(takeUntil(this.disposing$)).subscribe((activeProfile: Profile) => {
      //console.log('Active Profile Changed, creating ux', activeProfile);
      const currentExperience = new UserExperience(this.currentUserExperienceSubject.value); /* qry */
      if (debug) console.log(`[UX] Testing New User experience with Current/New`, activeProfile, currentExperience);
      let changedAny = false;
      // console.log('activeProfile', activeProfile);
      if (!activeProfile) {
        //console.log('No active profile');
        layoutService.hideMenu();
      } else if (!currentExperience.activeEmployeeID && activeProfile.id) {
        layoutService.showSidebar();
      }
      if (
        activeProfile?.id != currentExperience?.activeEmployeeID ||
        activeProfile?.features != currentExperience?.activeEmployee?.features ||
        activeProfile?.roles != currentExperience?.activeEmployee?.roles
      ) {
        // if (debug) console.log(`[UX] Testing New User experience ID changed`);
        currentExperience.activeEmployeeID = activeProfile?.id;
        currentExperience.activeEmployee = activeProfile;
        //changes sidebar when active employee is changed/logged out
        changedAny = true;
      }
      if (activeProfile?.endOfWeek != currentExperience.endOfWeek) {
        if (debug) console.log(`[UX] Testing New User experience end of week changed`);
        currentExperience.endOfWeek = activeProfile?.endOfWeek;
        changedAny = true;
      }
      if (activeProfile?.endOfScheduleWeek != currentExperience.endOfScheduleWeek) {
        if (debug) console.log(`[UX] Testing New User experience End of Schedule week changed`);
        currentExperience.endOfScheduleWeek = activeProfile?.endOfScheduleWeek;
        changedAny = true;
      }
      if (activeProfile?.endOfIvmrWeek != currentExperience.endOfIvmrWeek) {
        if (debug) console.log(`[UX] Testing New User experience End of Ivmr week changed`);
        currentExperience.endOfIvmrWeek = activeProfile?.endOfIvmrWeek;
        changedAny = true;
      }
      if (activeProfile?.overrideReconcileLock != currentExperience.overrideReconcileLock) {
        if (debug) console.log(`[UX] Testing New User experience Override lock changed`);
        currentExperience.overrideReconcileLock = activeProfile?.overrideReconcileLock;
        changedAny = true;
      }
      if (activeProfile?.delegatee != currentExperience.delegatee) {
        if (debug) console.log(`[UX] Testing New User experience delegatee changed`);
        currentExperience.delegatee = activeProfile?.delegatee;
        changedAny = true;
      }
      if (activeProfile?.roles != currentExperience.roles) {
        if (debug) console.log(`[UX] Testing New User experience Roles`);
        currentExperience.roles = activeProfile?.roles;
        changedAny = true;
      }
      if (activeProfile?.managedDivisionIds != currentExperience.managedDivisionIds) {
        if (debug) console.log(`[UX] Testing New User experience Managed Divisions`);
        currentExperience.managedDivisionIds = activeProfile?.managedDivisionIds;
        changedAny = true;
      }
      if (!activeProfile?.wage || activeProfile?.wage.isEqual(currentExperience.wage) === false) {
        if (debug) console.log(`[UX] Testing New User experience Roles`);
        currentExperience.wage = activeProfile?.wage;
        changedAny = true;
      }
      if (
        !activeProfile?.divisionConfig ||
        activeProfile.divisionConfig.isEquivalent(currentExperience?.divisionConfig) == false
      ) {
        if (debug) console.log(`[UX] Testing New User experience New Display Parameters`);
        currentExperience.divisionConfig = activeProfile?.divisionConfig;
        changedAny = true;
      }
      if (activeProfile?.systemAccount != currentExperience.systemAccount) {
        if (debug) console.log(`[UX] Testing New User experience Sys account`);
        currentExperience.systemAccount = activeProfile?.systemAccount;
        changedAny = true;
      }
      if (activeProfile?.payrollFormat != currentExperience.payrollFormat) {
        if (debug) console.log(`[UX] Testing New User experience ID payroll format`);
        currentExperience.payrollFormat = activeProfile?.payrollFormat;
        changedAny = true;
      }
      if (changedAny) {
        if (debug) console.log(`[UX] Auth Changed so rePublishing:`, currentExperience);
        this.currentUserExperienceSubject.next(currentExperience);
      }
    });
    // Auto Save
    this.currentUserExperienceSubject.subscribe(() => this.saveUserExperience()); // No Unsubscribe
    //Moved from profile.component
    //console.log("adding event listener for before install")
    window.addEventListener('beforeinstallprompt', (e) => {
      //console.log('got event listener for before install');
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      this.deferredPrompt = e;
    });
  }
  override get$(key: keyof UserExperience): Observable<UserExperience[keyof UserExperience]> {
    return this.currentUserExperience?.pipe(map((ux) => ux[key])) ?? of(undefined);
  }
  get(key: keyof UserExperience): UserExperience[keyof UserExperience] {
    return this.getUserExperience()[key] ?? undefined;
  }
  set(key: keyof UserExperience, value: UserExperience[keyof UserExperience]) {
    console.log(`[UX] setting new ux`);
    const newExperience: UserExperience = new UserExperience({
      ...this.currentUserExperienceSubject.getValue(),
      [key]: value
    });
    if (debug) console.log(`[UX] Set Called so rePublishing:`, newExperience);
    this.currentUserExperienceSubject.next(newExperience);
  }
  /*
    public get currentUserExperienceValue(): UserExperience {
        return this.currentUserExperienceSubject.value;
    }
    */
  public get driverLogs(): Observable<boolean> {
    return this.features.pipe(map((features) => features?.driverLogs === true));
  }

  public get noDriverLogs(): Observable<boolean> {
    return this.features.pipe(map((features) => features?.driverLogs !== true));
  }
  public get isSysAccount(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isSysAccount()));
  }
  public get isSysAdmin(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isSysAdmin()));
  }
  public get isAdmin(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isAdmin()));
  }
  public get isPayroll(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isPayroll()));
  }
  public get isBackOffice(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isBackOffice()));
  }
  public get isManager(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isManager()));
  }
  public get isManagerOnly(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isManagerOnly()));
  }
  public get canEditRouteInfo(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.canEditRouteInfo()));
  }
  public get isLeadDriver(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isLeadDriver()));
  }
  public get isMechanic(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isMechanic()));
  }
  public get isDriverOnly(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux && ux.isDriverOnly()));
  }

  public get features(): Observable<TenantFeatures> {
    return this.currentUserExperience.pipe(map((ux) => ux.activeEmployee?.features));
  }

  public get hasNoPayrollAccess(): Observable<boolean> {
    return combineLatest([
      this.isBackOffice.pipe(map((isBackOffice) => isBackOffice)),
      this.features.pipe(map((x) => x.payroll))
    ]).pipe(map(([x, y]) => !(x && y)));
  }

  public override getUserExperience(): UserExperience {
    return this.currentUserExperienceSubject.value;
  }

  private saveUserExperience() {
    localStorage.setItem('currentUserExperience', JSON.stringify(this.currentUserExperienceSubject.value));
  }

  public refreshClient() {
    this.crud ??= ServiceLocator.injector.get(CrudService);
    const loadExperience: UserExperience = JSON.parse(localStorage.getItem('currentUserExperience') ?? 'null');
    if (loadExperience) {
      delete loadExperience.activeEmployee;
      delete loadExperience.activeEmployeeID;
      delete loadExperience.divisionConfig;
      //localStorage.setItem('currentUserExperience', JSON.stringify(loadExperience));
    }
    this.crud.clear();
    window.location.reload();
  }

  get showCompanySelection(): boolean {
    return this.currentUserExperienceSubject.value.activeEmployee?.showCompanySelection() ?? false;
  }
  get showCompanySelection$(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux.activeEmployee?.showCompanySelection() ?? false));
  }

  public ShowEmployeeSelection$(): Observable<boolean> {
    return this.currentUserExperience.pipe(map((ux) => ux.isDelegatee() || ux.isManager()));
  }
  changeDefaultView(defaultView: ViewChoices) {
    console.trace('ux: change default view, new ux');
    const newUserExperience: UserExperience = new UserExperience(this.currentUserExperienceSubject.value);
    newUserExperience.defaultView = defaultView;
    if (debug) console.log(`[UX] Changed Default View so rePublishing:`, newUserExperience);
    this.currentUserExperienceSubject.next(newUserExperience);
  }
  updateMenuSelection(item: SummitMenuItem<NavMenuItem>) {
    const newUserExperience: UserExperience = new UserExperience(this.currentUserExperienceSubject.value);
    if (newUserExperience.menuBarSelection !== item) {
      if (debug) console.log('updating menu bar selection', item);
      newUserExperience.menuBarSelection = item;
      if (this.layoutService?.isScreenSmOrSmaller()) {
        //console.log('isScreenSmOrSmaller closing menu');
        this.layoutService.hideMenu();
      }
      if (debug) console.log(`[UX] Changed Menu Item Select so rePublishing:`, newUserExperience);
      //console.trace('new user experience', newUserExperience);
      this.currentUserExperienceSubject.next(newUserExperience);
      //added
    }
  }

  override refresh() {
    this.authService.refreshToken();
  }

  reset() {
    console.trace('Resetting User Experience');
    localStorage.removeItem('currentUserExperience');
    if (debug) console.log(`[UX] Reset so rePublishing:`);
    this.currentUserExperienceSubject.next(new UserExperience());
  }

  // preLoadBasedOnUx(newExperience?: UserExperience, force = false) {
  //   if (!newExperience) {
  //     newExperience = this.currentUserExperienceSubject.value;
  //   }
  //   if (!newExperience.isLoggedIn()) {
  //     this.preloadService.preLoadRole('None', force);
  //   } else {
  //     if (newExperience.isSysAdmin()) {
  //       this.preloadService.preLoadRole('SysAdmin', force);
  //     } else if (newExperience.isAdmin()) {
  //       this.preloadService.preLoadRole('Admin', force);
  //     } else if (newExperience.isManager()) {
  //       this.preloadService.preLoadRole('Manager', force);
  //     } else this.preloadService.preLoadRole('Driver', force);
  //   }
  // }

  //Should pull in protection from profile.component.ts
  async getInstalledApps(): Promise<any[]> {
    const lclNavigator = navigator as unknown as NavigatorApp;
    try {
      return lclNavigator && lclNavigator.getInstalledRelatedApps ? await lclNavigator.getInstalledRelatedApps() : [];
    } catch (e) {
      return [];
    }
  }

  installApp() {
    //console.log('Testing for deferred prompt');
    if (this.deferredPrompt) {
      //console.log("Prompting Install")
      this.deferredPrompt.prompt();
      //console.log(this.deferredPrompt);
      this.deferredPrompt.userChoice.then(this.installAppResult.bind(this));
    }
  }

  private installAppResult(choiceResult: any) {
    if (choiceResult?.outcome === 'accepted') {
      console.log('Your PWA has been installed');
    } else {
      console.log('User chose to not install your PWA');
    }
    if (this != null) {
      //Got error from Raygun on 'this' being null
      this.deferredPrompt = null;
    }
  }

  showInstall(): boolean {
    return this.deferredPrompt != null;
  }
  getDivisionPolicies(): Observable<DivisionPolicy> {
    // map observable to different observable
    return this.crud.get('Divisions/Policies/').pipe(
      map((result) => {
        //console.log(result);
        return result.data as DivisionPolicy;
      })
    );
  }
}
